import {
  DocumentProcessingClause,
  DocumentProcessingPagedReviewResponse,
  FlagType,
} from '../services/document-processing-api.service';

export interface DocumentPlaybookComplianceScore {
  score: number;
  greenFlagsCount: number;
  orangeFlagsCount: number;
  redFlagsCount: number;
  clauses: DocumentProcessingClause[];
}

export interface DocumentReviewMetric {
  score: number | null;
  matched: number;
  warnings: number;
  failed: number;
  needsReview: boolean;
}

export interface DocumentReviewPagesData {
  pages?: DocumentProcessingPagedReviewResponse[];
  pagedReviewData?: DocumentProcessingPagedReviewResponse[];
  clauses?: DocumentProcessingClause[];
}

export type DocumentReviewData =
  | DocumentProcessingPagedReviewResponse
  | DocumentProcessingPagedReviewResponse[]
  | DocumentReviewPagesData
  | null;

type DocumentReviewSource = DocumentReviewData | DocumentPlaybookComplianceScore[];

export interface BatchUpdateDocumentDto {
  uniqueId: string;
  assignedPlaybookId?: string;
  type: number;
  userPositionId: number;
  parties: number[];
  status: number;
  languageId: number;
}

export interface GetDocumentDto {
  uniqueId: string;
  assignedPlaybookId: string | null;
  name: string;
  sharedLink: string;
  directLink: string;
  type: number;
  revisionOption: number;
  lastModifiedAt: string;
  lastModifiedBy: string;
  userPositionId: number;
  parties: number[];
  status: number;
  languageId: number;
  contentType: DocumentContentType;
  playbookComplianceScores?: DocumentPlaybookComplianceScore[];
  reviewResponseData?: DocumentReviewData;
}

export interface GetDocumentDtoExtended extends GetDocumentDto {
  counterparties: number[];
  myPosition: number;
}

export interface DocumentVersionDto {
  versionNumber: number;
  createdAt: string;
  updatedAt: string;
  createdByUserId: number;
  createdByUserName: string;
  modifiedByUserId: number;
  modifiedByUserName: string;
  isCurrentVersion: boolean;
  documentName: string;
}

export interface GetDocumentVersionDto extends DocumentVersionDto, Partial<GetDocumentDto> {}

export interface CreateDocumentVersionDto {
  fileId: string;
}

export interface DocumentCommonDto {
  fileId?: string;
  assignedPlaybookId?: string;
  properties: DocumentPropertiesDto;
}

export interface CreateDocumentDto {
  fileId: string;
  assignedPlaybookId?: string;
  properties: DocumentPropertiesDto;
}

export interface DocumentPropertiesDto {
  name: string;
  type: number;
  revisionOption: number;
  userPositionId: number;
  parties: number[];
  status: number;
  languageId: number;
}

export interface DocumentSearchModel {
  documentName?: string;
  documentType?: number[];
  counterparty?: number[];
  position?: number[];
  status?: number[];
  languageId: number[];
}

export interface DocumentDetailsTriggerModel {
  uniqueId: string;
  focusField?: DocumentDetailsField;
  documentName?: string;
}

export enum DocumentDetailsField {
  DocumentType = 'documentType',
  Counterparties = 'counterparties',
  Position = 'myPosition',
  Status = 'status',
  Report = 'report',
  SignatureRequest = 'signatureRequest',
  Playbook = 'playbook',
  PlaybookCompliance = 'playbookCompliance',
}

export enum DocumentOperation {
  Review = 'review',
  Query = 'query',
  AddClauses = 'addClauses',
}

export enum DocumentContentType {
  DOCX = 1,
  PDF = 2,
}

export function buildDocumentPlaybookComplianceScores(
  clauses: DocumentProcessingClause[],
): DocumentPlaybookComplianceScore[] {
  if (!clauses.length) {
    return [];
  }

  const greenFlagsCount = clauses.filter(
    (clause) => clause.flagType === FlagType.MostDesired,
  ).length;
  const orangeFlagsCount = clauses.filter((clause) => clause.flagType === FlagType.Fallback).length;
  const redFlagsCount = clauses.filter(
    (clause) => clause.flagType === FlagType.Unacceptable || clause.flagType === FlagType.Missing,
  ).length;
  const actualPoints = greenFlagsCount * 2 + orangeFlagsCount;
  const maxPoints = clauses.length * 2;

  return [
    {
      score: maxPoints ? (actualPoints / maxPoints) * 100 : 0,
      greenFlagsCount,
      orangeFlagsCount,
      redFlagsCount,
      clauses,
    },
  ];
}

export function getDocumentPlaybookComplianceScores(
  reviewResponseData: DocumentReviewSource | undefined,
): DocumentPlaybookComplianceScore[] {
  if (!reviewResponseData) {
    return [];
  }

  if (Array.isArray(reviewResponseData)) {
    if (!reviewResponseData.length) {
      return [];
    }

    if (isDocumentPlaybookComplianceScore(reviewResponseData[0])) {
      return reviewResponseData as DocumentPlaybookComplianceScore[];
    }

    return buildDocumentPlaybookComplianceScores(
      (reviewResponseData as DocumentProcessingPagedReviewResponse[]).flatMap(
        (page) => page.clauses || [],
      ),
    );
  }

  if (isDocumentProcessingPagedReviewResponse(reviewResponseData)) {
    return buildDocumentPlaybookComplianceScores(reviewResponseData.clauses || []);
  }

  if (Array.isArray(reviewResponseData.pages)) {
    return buildDocumentPlaybookComplianceScores(
      reviewResponseData.pages.flatMap((page) => page.clauses || []),
    );
  }

  if (Array.isArray(reviewResponseData.pagedReviewData)) {
    return buildDocumentPlaybookComplianceScores(
      reviewResponseData.pagedReviewData.flatMap((page) => page.clauses || []),
    );
  }

  if (Array.isArray(reviewResponseData.clauses)) {
    return buildDocumentPlaybookComplianceScores(reviewResponseData.clauses);
  }

  return [];
}

export function getDocumentReviewMetric(
  reviewResponseData: DocumentReviewSource | undefined,
): DocumentReviewMetric | null {
  const reviewScores = getDocumentPlaybookComplianceScores(reviewResponseData);

  if (!reviewScores.length) {
    return null;
  }

  const matched = reviewScores.reduce((total, score) => total + score.greenFlagsCount, 0);
  const warnings = reviewScores.reduce((total, score) => total + score.orangeFlagsCount, 0);
  const failed = reviewScores.reduce((total, score) => total + score.redFlagsCount, 0);
  const totalClauses = matched + warnings + failed;
  const actualPoints = matched * 2 + warnings;
  const score = totalClauses ? Math.round((actualPoints / (totalClauses * 2)) * 100) : null;

  return {
    score,
    matched,
    warnings,
    failed,
    needsReview: false,
  };
}

function isDocumentPlaybookComplianceScore(
  value: unknown,
): value is DocumentPlaybookComplianceScore {
  return (
    !!value &&
    typeof value === 'object' &&
    'greenFlagsCount' in value &&
    'orangeFlagsCount' in value &&
    'redFlagsCount' in value &&
    'clauses' in value
  );
}

function isDocumentProcessingPagedReviewResponse(
  value: unknown,
): value is DocumentProcessingPagedReviewResponse {
  return (
    !!value &&
    typeof value === 'object' &&
    'pageNumber' in value &&
    'totalPages' in value &&
    'clauses' in value
  );
}
